import { ChevronDownIcon, Cross2Icon } from '@radix-ui/react-icons';
import { cn } from '@utils/index';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface SearchableMultiSelectOption {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
  group?: string;
}

interface SearchableMultiSelectGroup {
  label: string;
  options: SearchableMultiSelectOption[];
}

interface SearchableMultiSelectProps {
  options?: SearchableMultiSelectOption[];
  groups?: SearchableMultiSelectGroup[];
  selected: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  clearAllLabel?: string;
  className?: string;
  contentClassName?: string;
  disabled?: boolean;
  maxCount?: number;
  icon?: React.ComponentType<{ className?: string }>;
  renderOption?: (option: SearchableMultiSelectOption) => React.ReactNode;
  onOpenChange?: (open: boolean) => void;
  showClearAll?: boolean;
  showSelectAll?: boolean;
}

export function SearchableMultiSelect({
  options = [],
  groups = [],
  selected,
  onChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No items found.',
  clearAllLabel = 'Clear all',
  className,
  contentClassName,
  disabled = false,
  maxCount = 4,
  icon: Icon,
  renderOption,
  onOpenChange: onOpenChangeProp,
  showClearAll = true,
  showSelectAll = false,
}: SearchableMultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const organizedGroups = React.useMemo(() => {
    if (groups.length > 0) {
      return groups;
    }

    if (options.length === 0) {
      return [];
    }

    const groupedOptions = new Map<string, SearchableMultiSelectOption[]>();

    options.forEach(option => {
      const key = option.group || 'default';
      if (!groupedOptions.has(key)) {
        groupedOptions.set(key, []);
      }
      const existing = groupedOptions.get(key);
      if (existing) {
        existing.push(option);
      }
    });

    return Array.from(groupedOptions.entries()).map(([label, grouped]) => ({
      label: label === 'default' ? '' : label,
      options: grouped,
    }));
  }, [groups, options]);

  const handleOpenChange = (value: boolean) => {
    setOpen(value);
    onOpenChangeProp?.(value);

    if (!value) {
      setSearch('');
    }
  };

  const handleSelect = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter(item => item !== value));
      return;
    }

    onChange([...selected, value]);
  };

  const handleClear = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    onChange([]);
  };

  const allOptions = React.useMemo(
    () => organizedGroups.flatMap(group => group.options),
    [organizedGroups],
  );

  const allSelected =
    allOptions.length > 0 && allOptions.every(option => selected.includes(option.value));

  const displayValue = React.useMemo(() => {
    if (selected.length === 0) {
      return placeholder;
    }

    if (selected.length <= maxCount) {
      return selected
        .map(value => allOptions.find(option => option.value === value)?.label)
        .filter(Boolean)
        .join(', ');
    }

    return `${selected.length} selected`;
  }, [allOptions, maxCount, placeholder, selected]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          disabled={disabled}
          className={cn('w-full justify-between', className)}
        >
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {Icon && <Icon className="h-4 w-4 shrink-0 opacity-70" />}
            <span className="flex-1 truncate text-left font-normal">{displayValue}</span>
          </div>
          <div className="ml-2 flex shrink-0 items-center">
            {selected.length > 0 && (
              <>
                <Cross2Icon
                  className="mr-2 h-4 w-4 opacity-50 hover:opacity-100"
                  onClick={event => handleClear(event)}
                  aria-label="Clear selection"
                  role="button"
                />
                <div className="mr-2 h-4 w-px bg-border" />
              </>
            )}
            <ChevronDownIcon className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('w-full min-w-[240px] p-0', contentClassName)} align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            {showSelectAll && allOptions.length > 0 && (
              <CommandGroup>
                <CommandItem
                  value="Select all"
                  onSelect={() => onChange(allSelected ? [] : allOptions.map(o => o.value))}
                  className="gap-3"
                >
                  <Checkbox checked={allSelected} className="pointer-events-none" />
                  <span>Select all</span>
                </CommandItem>
              </CommandGroup>
            )}
            {organizedGroups.map((group, index) => (
              <CommandGroup key={group.label || `group-${index}`} heading={group.label}>
                {group.options.map(option => {
                  const isSelected = selected.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      onSelect={() => handleSelect(option.value)}
                      className="gap-3"
                    >
                      <Checkbox checked={isSelected} className="pointer-events-none" />
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        {renderOption ? (
                          renderOption(option)
                        ) : (
                          <>
                            {option.icon && <option.icon className="h-4 w-4 shrink-0" />}
                            <span className="truncate">{option.label}</span>
                          </>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
            {showClearAll && selected.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    value={clearAllLabel}
                    onSelect={() => {
                      onChange([]);
                    }}
                  >
                    <Cross2Icon className="h-4 w-4" />
                    <span>
                      {clearAllLabel} ({selected.length})
                    </span>
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
